import { EFinancingCompany, ESourceSystem } from '@if/core/types'
import { ControlledTextField } from '@if/mui'
import CloseIcon from '@mui/icons-material/Close'
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material'
import { AsyncActionButton } from '@src/components'
import { useSelectedCompany } from '@src/contexts/SelectedCompanyProvider'
import { useCreateTerritory, useTerritoriesList } from '@src/data/api/territories-api/territories-api'
import { TerritoryFilter } from '@src/data/types/Filter'
import { Territory } from '@src/data/types/Territory'
import { memo, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  title: string
  handleClose: () => void
}

const AddTerritoryDialog = ({ open, handleClose, title }: Props) => {
  const { t } = useTranslation()
  const [createTerritory, isCreatingTerritory, resetCreateTerritory] = useCreateTerritory()
  const selectedCompany = useSelectedCompany()
  const [nameExists, setNameExists] = useState(false)

  const query: TerritoryFilter = useMemo(() => {
    return {
      financingCompanyId: selectedCompany,
    } as TerritoryFilter
  }, [selectedCompany])

  const [territoriesList] = useTerritoriesList(query)

  const { handleSubmit, reset, control } = useForm<Territory>({
    mode: 'onBlur',
  })

  useEffect(() => {
    reset()
    setNameExists(false)
  }, [open, reset])

  const onSubmit = async (data: Territory) => {
    setNameExists(territoriesList.some((territory) => territory.name === data.name))
    if (nameExists) {
      return
    }
    if (selectedCompany === EFinancingCompany.IFinance) {
      data.sourceSystem = ESourceSystem.Alis
      data.financingCompanyId = EFinancingCompany.IFinance
    } else return
    await createTerritory(data).then((value) => {
      resetCreateTerritory()
      if (value) handleClose()
    })
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose()
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      transitionDuration={0}
    >
      <DialogTitle style={{ borderBottom: '1px solid' }} id="alert-dialog-title" sx={{ textAlign: 'center' }}>
        <IconButton
          aria-label="close"
          onClick={() => {
            handleClose()
          }}
          style={{
            position: 'absolute',
            right: 15,
            top: 15,
          }}
        >
          <CloseIcon />
        </IconButton>
        {title}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <ControlledTextField name="name" control={control} id="name" label={t('common.name')} fullWidth />
          </Grid>
        </Grid>
      </DialogContent>
      {nameExists && (
        <Alert severity="error" style={{ margin: 0 }}>
          {t('browseTerritories.mainActions.territoryNameExists')}
        </Alert>
      )}
      <DialogActions>
        <Grid container spacing={1} justifyContent="center" textAlign="center">
          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <AsyncActionButton
              fullWidth
              variant="outlined"
              isPending={isCreatingTerritory}
              onClick={async () => {
                await handleSubmit(onSubmit)()
              }}
            >
              {t('common.addTerritory')}
            </AsyncActionButton>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

export default memo(AddTerritoryDialog)
